import { AxiosError } from "axios";
import { Credentials } from "~/src/contexts/AuthContext/AuthProvider";
import { OmradeType } from "../helpers/types";
import {
  HjorteviltBrukereV1AutentiseringSharedTokenResponse, 
  HjorteviltBrukereV1JaktomraderCreateJaktomradeCreateJaktomradeRequest, 
  HjorteviltBrukereV1JaktomraderSharedJaktomradeResponse,
  HjorteviltBrukereV1JegerOpplysningerSharedJegerOpplysningerResponse,
  HjorteviltBrukereV1JegerOpplysningerUpdateJegerOpplysningerUpdateJegerOpplysningerRequest,
  HjorteviltJaktdataV1IndividCreateIndividCreateIndividRequest,
  HjorteviltJaktdataV1IndividSharedIndividResponse,
  HjorteviltJaktdataV1IndividUpdateIndividUpdateIndividRequest,
  HjorteviltJaktdataV1JaktdagCreateJaktdagMedIndividCreateJaktdagMedIndividRequest,
  HjorteviltJaktdataV1JaktdagSharedJaktdagResponse,
  HjorteviltJaktdataV1JaktdagUpdateJaktdagUpdateJaktdagRequest,
  HjorteviltJaktdataV1UpGetUpResponse,
  HjorteviltOmraderV1OmradeListerGetAlleJaktfeltJaktfeltResponse,
  HjorteviltOmraderV1OmradeListerGetAlleValdValdResponse,
  HjorteviltOmraderV1OmradeListerGetAlleVillreinvaldVillreinvaldResponse,
} from "./generated";

export type ClientState = "Online" | "Offline" | "Degraded" | "NotAuthenticated" | "Loading";

export const NETWORK_ERROR_CODES = [
  "ERR_NETWORK",
  "ECONNABORTED",
  "ETIMEDOUT",
  "ERR_INTERNET_DISCONNECTED",
];

export const NOT_AUTHENTICATED_STATUS_CODES = [401, 403];
export const NOT_CACHED_STATUS_CODE = 418;
export const NO_CONNECTION_STATUS_CODE = -1;

export type Områdekode = CreateJaktomradeRequest;
export type Favorittområde = {
  isPending: boolean;
} & JaktomradeResponse;
export type Område = JaktfeltResponse | ValdResponse | VillreinvaldResponse;

export type OmrådeData = {
  type: OmradeType;
  data: Område[];
};

export type IndividResponse = HjorteviltJaktdataV1IndividSharedIndividResponse;
export type JaktdagResponse = HjorteviltJaktdataV1JaktdagSharedJaktdagResponse;
export type JaktfeltResponse = HjorteviltOmraderV1OmradeListerGetAlleJaktfeltJaktfeltResponse;
export type ValdResponse = HjorteviltOmraderV1OmradeListerGetAlleValdValdResponse;
export type VillreinvaldResponse =
  HjorteviltOmraderV1OmradeListerGetAlleVillreinvaldVillreinvaldResponse;
export type CreateJaktdagRequest =
  HjorteviltJaktdataV1JaktdagCreateJaktdagMedIndividCreateJaktdagMedIndividRequest;
export type UpdateJaktdagRequest = HjorteviltJaktdataV1JaktdagUpdateJaktdagUpdateJaktdagRequest;
export type JaktomradeResponse = HjorteviltBrukereV1JaktomraderSharedJaktomradeResponse;
export type JegerOpplysningerResponse =
  HjorteviltBrukereV1JegerOpplysningerSharedJegerOpplysningerResponse;
export type TokenResponse = HjorteviltBrukereV1AutentiseringSharedTokenResponse;
export type RefreshTokenResponse = HjorteviltBrukereV1AutentiseringSharedTokenResponse;
export type UpdateJegerOpplysningerRequest =
  HjorteviltBrukereV1JegerOpplysningerUpdateJegerOpplysningerUpdateJegerOpplysningerRequest;
export type CreateIndividRequest = HjorteviltJaktdataV1IndividCreateIndividCreateIndividRequest;
export type CreateJaktomradeRequest =
    HjorteviltBrukereV1JaktomraderCreateJaktomradeCreateJaktomradeRequest;
export type UpdateIndividRequest = HjorteviltJaktdataV1IndividUpdateIndividUpdateIndividRequest;
export type UpData = HjorteviltJaktdataV1UpGetUpResponse;
export type IndividMedJaktdagResponse = {
  Jaktdag?: JaktdagResponse;
} & IndividResponse;

export type Individ = IndividMedJaktdagResponse & CreateIndividRequest;

export type Jaktdag = Omit<CreateJaktdagRequest, "Individer"> &
  Omit<JaktdagResponse, "Id" | "Dato" | "Art" | "Jaktfelt" | "Individer"> & {
    Individer: Individ[];
  };

export type ApiError = {
  statusCode: number;
  message: string;
  axiosError?: AxiosError;
};

export type JaktdataApi = {
  login: (credentials: Credentials) => Promise<TokenResponse>;
  refreshToken: () => Promise<TokenResponse>;
  getClientState: () => Promise<ClientState>;
  getJegeropplysninger: () => Promise<JegerOpplysningerResponse>;
  putJegeropplysninger: (
    jegeropplysninger: UpdateJegerOpplysningerRequest,
  ) => Promise<JegerOpplysningerResponse>;
  getJaktfelt: () => Promise<OmrådeData>;
  getVald: () => Promise<OmrådeData>;
  getVillreinVald: () => Promise<OmrådeData>;
  getJaktområder: () => Promise<Favorittområde[]>;
  postJaktområde: (områdekode: Områdekode) => Promise<JaktomradeResponse>;
  deleteJaktområde: (områdekode: Områdekode) => Promise<void>;
  getJaktdager: (sesong: number, jaktfeltId: string) => Promise<JaktdagResponse[]>;
  getJaktdag: (jaktdagId: string) => Promise<JaktdagResponse>;
  deleteJaktdag: (jaktdagId: string) => Promise<void>;
  postJaktdag: (jaktdag: CreateJaktdagRequest) => Promise<string>;
  putJaktdag: (jaktdag: CreateJaktdagRequest) => Promise<void>;
  putJaktdagWithoutIndivid: (jaktdag: CreateJaktdagRequest) => Promise<void>;
  getSkutteDyr: (sesong: number, omradeId: string) => Promise<IndividResponse[]>;
  getIndivid: (individId: string) => Promise<IndividResponse>;
  putIndivid: (individ: UpdateIndividRequest) => Promise<void>;
  deleteIndivid: (individId: string) => Promise<void>;
  postIndivid: (individ: CreateIndividRequest) => Promise<string>;
};
